/**
 * SpeyBooks - Central API Client & Service Definitions
 * * Core communication layer between the React frontend and the SpeyBooks
 * RESTful API. Manages the Axios instance, JWT lifecycle (including
 * automatic silent token refresh), and standardizes the ApiResponse envelope.
 * * Structural Highlights:
 * - Request Interceptors: Injects Bearer tokens, proactive refresh when token
 *   is within the expiry skew window, and standardizes Content-Type.
 * - Response Interceptors: Implements the _retry pattern for 401 Unauthorized
 * handling and automated logout on refresh failure.
 * - Service Modules: Grouped functional objects (auth, dashboard, invoices,
 * quotes, etc.) covering 198+ endpoints.
 * * Data Integrity:
 * - Enforces the "Minor Units Only" boundary for all monetary parameters.
 * - Integrates TMADD (Universal Import Engine) v3.0 - v6.0 metadata support.
 * * @module lib/api
 * @author William Murray
 * @version 5.10.4
 * @copyright (c) 2026 SpeyTech Labs LTD. All rights reserved.
 */

import axios, { AxiosError } from 'axios';
import type { DashboardSummary } from './dashboard-summary';

// Types
// Types
export interface UserOrganisation {
  id: number;
  name: string;
  plan: 'sole_trader' | 'limited_company' | 'trial';
}

export interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  totpEnabled?: boolean;
  isAdmin?: boolean;
  organisation?: UserOrganisation | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Token storage
const TOKEN_KEY = 'speytech_access_token';
const REFRESH_KEY = 'speytech_refresh_token';

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setTokens = (access: string, refresh: string) => {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

// Create axios instance
const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Endpoints that should NOT trigger token refresh on 401 or proactive refresh.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh'];

// Refresh this many seconds before expiry. The access token is 15 min;
// refreshing inside the last minute kills the 401-bounce without refreshing
// on every request.
const REFRESH_SKEW_SECONDS = 60;

// Read the exp claim (seconds since epoch) from a JWT without verifying it.
// The server verifies; this only decides whether to refresh ahead of the 401.
// null means missing or malformed -- treated as "do not proactively refresh",
// the reactive path then handles it.
function getTokenExp(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function accessTokenExpiringSoon(): boolean {
  const exp = getTokenExp(getAccessToken());
  if (exp === null) return false;
  return exp - Math.floor(Date.now() / 1000) <= REFRESH_SKEW_SECONDS;
}

// Single in-flight refresh promise shared across all concurrent callers.
// A burst of requests near expiry triggers one POST, all await it.
//
// BOUNDARY: per-tab dedupe is safe only because /auth/refresh does not rotate
// the refresh token (the response carries accessToken only). If rotation is
// ever added, two tabs refreshing concurrently would invalidate each other and
// this must go cross-tab (BroadcastChannel or localStorage lock).
let refreshInFlight: Promise<string | null> | null = null;

function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  // Raw axios: must not pass back through the interceptors.
  refreshInFlight = axios
    .post<ApiResponse<{ accessToken: string }>>('/api/v1/auth/refresh', { refreshToken })
    .then((r) => {
      if (r.data.success && r.data.data) {
        localStorage.setItem(TOKEN_KEY, r.data.data.accessToken);
        return r.data.data.accessToken;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => { refreshInFlight = null; });

  return refreshInFlight;
}

// Request interceptor: proactive refresh when token is inside the skew window,
// then attach the (possibly just-refreshed) token. Proactive is best-effort;
// a failed refresh here swallows and falls through with the old token. The
// reactive 401 path below is the sole logout authority.
api.interceptors.request.use(async (config) => {
  const isAuthEndpoint = config.url && AUTH_ENDPOINTS.some((ep) => config.url?.includes(ep));
  if (!isAuthEndpoint && getAccessToken() && accessTokenExpiringSoon()) {
    await refreshAccessToken(); // best-effort; read fresh token below
  }
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    
    // Skip refresh logic for auth endpoints - let error propagate to caller
    const isAuthEndpoint = originalRequest?.url && 
      AUTH_ENDPOINTS.some(ep => originalRequest.url?.includes(ep));
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }
    
    // Item 57: the grace gate. A 402 means the organisation has no
    // active subscription, trial, or grace; the billing page is the
    // landing, not an error. Billing endpoints are allowlisted at the
    // server so this cannot loop; the path check is belt and braces.
    if (error.response?.status === 402) {
      if (!window.location.pathname.startsWith('/settings/billing')) {
        window.location.href = '/settings/billing';
        return new Promise(() => {}); // navigation in flight; suppress error UI
      }
      return Promise.reject(error);
    }
    
    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Route through the shared primitive so proactive and reactive cannot
      // double-refresh and there is one refresh function, not two copies of
      // the POST. Also closes the edge where success:false from the server
      // left the caller logged in with a dead token that 401s forever.
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
      clearTokens();
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: async (data: { email: string; password: string; fullName: string; organisationName: string; plan?: string }) => {
    const response = await api.post<ApiResponse<{
      user: { id: number; email: string; fullName: string; emailVerified: boolean; createdAt: string };
      organisation: { id: string; name: string; slug: string };
      token: string;
      checkoutUrl?: string;
    }>>('/auth/register', data);
    return response.data;
  },

  login: async (username: string, password: string) => {
    const response = await api.post<ApiResponse<{ requiresTotp?: boolean; tempToken?: string } & AuthTokens>>(
      '/auth/login',
      { username, password }
    );
    return response.data;
  },
  
  verifyTotp: async (token: string, tempToken: string) => {
    const response = await axios.post<ApiResponse<AuthTokens>>(
      '/api/v1/auth/totp/verify',
      { token },
      { headers: { Authorization: `Bearer ${tempToken}` } }
    );
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearTokens();
    }
  },
  
  me: async () => {
    const response = await api.get<ApiResponse<User & { totpEnabled: boolean }>>('/auth/me');
    return response.data;
  },
  
  refresh: async (refreshToken: string) => {
    const response = await api.post<ApiResponse<{ accessToken: string }>>('/auth/refresh', { refreshToken });
    return response.data;
  },
  
  totpSetup: async () => {
    const response = await api.post<ApiResponse<{ secret: string; otpauth: string }>>('/auth/totp/setup');
    return response.data;
  },
  
  totpEnable: async (token: string) => {
    const response = await api.post<ApiResponse<void>>('/auth/totp/enable', { token });
    return response.data;
  },
  
  totpDisable: async (password: string) => {
    const response = await api.delete<ApiResponse<void>>('/auth/totp', { data: { password } });
    return response.data;
  },
  
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post<ApiResponse<void>>('/auth/password', { currentPassword, newPassword });
    return response.data;
  },
  
  forgotPassword: async (email: string) => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', { email });
    return response.data;
  },
  
  resetPassword: async (token: string, password: string) => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/reset-password', { token, password });
    return response.data;
  },
  
  validateResetToken: async (token: string) => {
    const response = await api.get<ApiResponse<{ valid: boolean; email?: string }>>(`/auth/reset-password/validate?token=${token}`);
    return response.data;
  },
  
  verifyEmail: async (token: string) => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/verify-email', { token });
    return response.data;
  },
  
  sendVerificationEmail: async () => {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/verify-email/send');
    return response.data;
  },
  
  getVerificationStatus: async () => {
    const response = await api.get<ApiResponse<{ verified: boolean; email: string; verifiedAt: string | null }>>('/auth/verify-email/status');
    return response.data;
  },
};

// Dashboard API
// Repointed under the retire-now cutover: the old inline /reports/dashboard handler
// was deleted in the Leg 5 cut. The sole dashboard surface is now the aggregator
// envelope at /reports/dashboard-summary (operationId getDashboardSummary). One
// request populates the whole page; the bands read from this one envelope.
export const dashboardApi = {
  getSummary: async () => {
    const response = await api.get<ApiResponse<DashboardSummary>>('/reports/dashboard-summary');
    return response.data;
  },
};

// Ledger verification API
// The chain-head verification given its own route so the shell strip
// (TopNavVerifiedStrip) can read it on any page, not only where the dashboard
// envelope is loaded. The same verification the aggregator carries in
// summary.ledger, exposed app-wide. The headline is status, asOf and head; the
// period chrome is optional and progressive, sent only when available, never
// fabricated, and the headline never waits on it.
export interface LedgerVerificationResponse {
  status: 'verified' | 'failed';
  asOf: string; // display-ready snapshot, formatted server-side
  head: string; // display-ready head, e.g. "#7f3ac9"
  periodLabel?: string;
  fyLabel?: string;
  lastWrite?: string;
}

export const verificationApi = {
  getState: async () => {
    const response = await api.get<ApiResponse<LedgerVerificationResponse>>('/reports/ledger-verification');
    return response.data;
  },
};

// Invoices API
export interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceType: 'sales' | 'purchase';
  contactId: string;
  contactName: string;
  issueDate: string;
  dueDate: string;
  subtotal: string;
  vatAmount: string;
  total: string;
  amountPaid: string;
  status: string;
  createdAt: string;
}

export interface InvoiceDetail extends Invoice {
  contact: {
    id: string;
    name: string;
    email: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country: string;
  };
  lines: {
    id: number;
    description: string;
    quantity: string;
    unitPrice: string;
    vatRate: number;
    vatAmount: string;
    lineTotal: string;
    accountId?: string;
    accountName?: string;
  }[];
  notes?: string;
  terms?: string;
}

export const invoicesApi = {
  list: async (params?: {
    type?: 'sales' | 'purchase';
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<ApiResponse<{
      invoices: Invoice[];
      stats: { draftCount: number; sentCount: number; overdueCount: number; outstandingTotal: string };
    }>>('/invoices', { params });
    return response.data;
  },
  
  sendEmail: async (id: string, options?: { to?: string; subject?: string; message?: string }) => {
    const response = await api.post<ApiResponse<{ sentTo: string; message: string }>>(`/invoices/${id}/email`, options || {});
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get<ApiResponse<InvoiceDetail>>(`/invoices/${id}`);
    return response.data;
  },
  
  create: async (data: {
    invoiceType: 'sales' | 'purchase';
    contactId: string;
    issueDate: string;
    dueDate: string;
    notes?: string;
    terms?: string;
    lines: { description: string; quantity: string; unitPrice: string; vatRate: number; accountId?: string }[];
  }) => {
    const response = await api.post<ApiResponse<{ id: string; invoiceNumber: string }>>('/invoices', data);
    return response.data;
  },

  preview: async (
    data: { lines: { quantity: string; unitPrice: string; vatRate: number }[] },
    opts?: { signal?: AbortSignal },
  ) => {
    const response = await api.post<ApiResponse<{
      lines: { net: number; vat: number; total: number }[];
      subtotal: number;
      vatTotal: number;
      total: number;
    }>>('/invoices/preview', data, { signal: opts?.signal });
    return response.data;
  }, 
  
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch<ApiResponse<void>>(`/invoices/${id}/status`, { status });
    return response.data;
  },
  
  recordPayment: async (id: string, amountPence: number) => {
    const response = await api.post<ApiResponse<{ newStatus: string }>>(`/invoices/${id}/payment`, { amount: amountPence });
    return response.data;
  },
  
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/invoices/${id}`);
    return response.data;
  },
  
  update: async (id: string, data: {
    contactId?: string;
    issueDate?: string;
    dueDate?: string;
    notes?: string;
    terms?: string;
    lines?: { description: string; quantity: string; unitPrice: string; vatRate: number; accountId?: string }[];
  }) => {
    const response = await api.put<ApiResponse<void>>(`/invoices/${id}`, data);
    return response.data;
  },
};

// Quotes API
export interface Quote {
  id: string;
  quoteNumber: string;
  contactId: string;
  contactName: string;
  issueDate: string;
  validUntil: string;
  subtotal: string;
  vatAmount: string;
  total: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'converted';
  createdAt: string;
}

export interface QuoteDetail extends Quote {
  contact: {
    id: string;
    name: string;
    email?: string;
    company?: string;
  };
  notes?: string;
  terms?: string;
  lines: {
    id: number;
    description: string;
    quantity: number;
    unitPrice: string;
    vatRate: number;
    lineTotal: string;
    accountId?: string;
    accountName?: string;
  }[];
  convertedInvoiceId?: string;
  convertedInvoiceNumber?: string;
}

export const quotesApi = {
  list: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<ApiResponse<{
      quotes: Quote[];
      stats: { draftCount: number; sentCount: number; acceptedCount: number; expiredCount: number };
    }>>('/quotes', { params });
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get<ApiResponse<QuoteDetail>>(`/quotes/${id}`);
    return response.data;
  },
  
  create: async (data: {
    contactId: string;
    issueDate: string;
    validUntil: string;
    notes?: string;
    terms?: string;
    lines: { description: string; quantity: string; unitPrice: string; vatRate: number; accountId?: string }[];
  }) => {
    const response = await api.post<ApiResponse<{ id: string; quoteNumber: string }>>('/quotes', data);
    return response.data;
  },
  
  update: async (id: string, data: {
    contactId?: string;
    issueDate?: string;
    validUntil?: string;
    notes?: string;
    terms?: string;
    lines?: { description: string; quantity: string; unitPrice: string; vatRate: number; accountId?: string }[];
  }) => {
    const response = await api.put<ApiResponse<void>>(`/quotes/${id}`, data);
    return response.data;
  },
  
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch<ApiResponse<void>>(`/quotes/${id}/status`, { status });
    return response.data;
  },
  
  convertToInvoice: async (id: string) => {
    const response = await api.post<ApiResponse<{ invoiceId: string; invoiceNumber: string }>>(`/quotes/${id}/convert`);
    return response.data;
  },
  
  preview: async (
    data: { lines: { quantity: string; unitPrice: string; vatRate: number }[] },
    opts?: { signal?: AbortSignal },
  ) => {
    const response = await api.post<ApiResponse<{
      lines: { net: number; vat: number; total: number }[];
      subtotal: number;
      vatTotal: number;
      total: number;
    }>>('/quotes/preview', data, { signal: opts?.signal });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/quotes/${id}`);
    return response.data;
  },
};

// Contacts API
export interface Contact {
  id: string;
  contactType: 'customer' | 'supplier' | 'both';
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  city?: string;
  country: string;
  isActive: boolean;
}

export const contactsApi = {
  list: async (params?: { type?: string; search?: string; active?: boolean }) => {
    const response = await api.get<ApiResponse<{ contacts: Contact[] }>>('/contacts', { params });
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get<ApiResponse<Contact>>(`/contacts/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Contact>) => {
    const response = await api.post<ApiResponse<{ id: string }>>('/contacts', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Contact>) => {
    const response = await api.put<ApiResponse<void>>(`/contacts/${id}`, data);
    return response.data;
  },
};

// Accounts API
export interface Account {
  id: string;
  code: string;
  name: string;
  accountType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  // Item 54: on the wire since the accounts route mapped them; the type
  // catches up so the journal picker can mirror the posting matrix.
  isControlAccount?: boolean;
  controlType?: string | null;
  parentId?: string;
  description?: string;
  defaultVatRate?: number;
  isActive: boolean;
  isSystem: boolean;
  children?: Account[];
}

export const accountsApi = {
  list: async (params?: { type?: string; active?: boolean; flat?: boolean }) => {
    const response = await api.get<ApiResponse<{ accounts: Account[] }>>('/accounts', { params });
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get<ApiResponse<Account>>(`/accounts/${id}`);
    return response.data;
  },
  
  getBalance: async (id: string) => {
    const response = await api.get<ApiResponse<{
      account: { id: string; code: string; name: string; accountType: string };
      balance: { debit: string; credit: string; net: string };
    }>>(`/accounts/${id}/balance`);
    return response.data;
  },
  
  create: async (data: {
    code: string;
    name: string;
    accountType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
    parentId?: string;
    description?: string;
    defaultVatRate?: number;
  }) => {
    const response = await api.post<ApiResponse<{ id: string }>>('/accounts', data);
    return response.data;
  },
  
  update: async (id: string, data: {
    code?: string;
    name?: string;
    description?: string;
    defaultVatRate?: number;
    isActive?: boolean;
  }) => {
    const response = await api.put<ApiResponse<void>>(`/accounts/${id}`, data);
    return response.data;
  },
};

// Transactions API
export interface Transaction {
  id: string;
  date: string;
  description: string;
  reference?: string;
  status: 'draft' | 'posted';
  isReconciled: boolean;
  totalDebit: string;
  totalCredit: string;
  createdByName: string;
}

export const transactionsApi = {
  list: async (params?: {
    from?: string;
    to?: string;
    accountId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<ApiResponse<{ transactions: Transaction[] }>>('/transactions', { params });
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get<ApiResponse<Transaction & {
      lines: {
        id: number;
        accountId: string;
        accountCode: string;
        accountName: string;
        amount: string;
        contactName?: string;
      }[];
    }>>(`/transactions/${id}`);
    return response.data;
  },
  
  create: async (data: {
    date: string;
    description: string;
    reference?: string;
    status?: 'draft' | 'posted';
    lines: Array<{
      accountId: string;
      amount: number;
      vatRate?: number;
      vatTreatment?: 'none' | 'inclusive' | 'exclusive';
      contactId?: string;
      description?: string;
    }>;
  }) => {
    const response = await api.post<ApiResponse<{ id: string }>>('/transactions', data);
    return response.data;
  },
  
  getLedger: async (accountId: string, params?: { from?: string; to?: string }) => {
    const response = await api.get<ApiResponse<{
      account: { id: string; code: string; name: string; accountType: string };
      openingBalance: string;
      entries: {
        transactionId: string;
        date: string;
        description: string;
        reference?: string;
        debit?: string;
        credit?: string;
        balance: string;
      }[];
      closingBalance: string;
    }>>(`/transactions/account/${accountId}`, { params });
    return response.data;
  },

  categorise: async (id: string, data: {
    accountId: string;
    vatRate?: number;
    vatTreatment?: string;
    contactId?: string;
  }) => {
    const response = await api.patch<ApiResponse<{
      id: string;
      categorised: boolean;
      account: { id: string; code: string; name: string };
    }>>(`/transactions/${id}/categorise`, data);
    return response.data;
  },

  suggestCategory: async (id: string) => {
    const response = await api.post<ApiResponse<{
      matched: boolean;
      suggestion: {
        accountId: string;
        accountCode: string;
        accountName: string;
        ruleName: string;
      } | null;
    }>>(`/transactions/${id}/suggest-category`);
    return response.data;
  },
};

// Reports API
export const reportsApi = {
  profitLoss: async (from: string, to: string) => {
    const response = await api.get<ApiResponse<{
      period: { from: string; to: string };
      revenue: { accounts: { code: string; name: string; balance: string }[]; total: string };
      expenses: { accounts: { code: string; name: string; balance: string }[]; total: string };
      netProfit: string;
    }>>('/reports/profit-loss', { params: { from, to } });
    return response.data;
  },
  
  balanceSheet: async (asOf: string) => {
    const response = await api.get<ApiResponse<{
      asOf: string;
      assets: { accounts: { code: string; name: string; balance: string }[]; total: string };
      liabilities: { accounts: { code: string; name: string; balance: string }[]; total: string };
      equity: { accounts: { code: string; name: string; balance: string }[]; total: string };
      balanced: boolean;
    }>>('/reports/balance-sheet', { params: { asOf } });
    return response.data;
  },
  
  trialBalance: async (asOf: string) => {
    const response = await api.get<ApiResponse<{
      asOf: string;
      accounts: { code: string; name: string; debit?: string; credit?: string }[];
      totals: { debit: string; credit: string; balanced: boolean };
    }>>('/reports/trial-balance', { params: { asOf } });
    return response.data;
  },
  
  vat: async (from: string, to: string) => {
    const response = await api.get<ApiResponse<{
      period: { from: string; to: string };
      outputVat: string;
      inputVat: string;
      netVat: string;
      payable: boolean;
    }>>('/reports/vat', { params: { from, to } });
    return response.data;
  },
  
  agedDebtors: async (asOfDate?: string) => {
    const response = await api.get<ApiResponse<{
      reportDate: string;
      reportTitle: string;
      contacts: {
        contactId: string;
        contactName: string;
        invoices: any[];
        current: string;
        days1to30: string;
        days31to60: string;
        days61to90: string;
        days90plus: string;
        total: string;
      }[];
      totals: {
        current: string;
        days1to30: string;
        days31to60: string;
        days61to90: string;
        days90plus: string;
        total: string;
      };
      summary: {
        totalContacts: number;
        totalInvoices: number;
      };
    }>>('/reports/aged-debtors', { params: asOfDate ? { asOfDate } : {} });
    return response.data;
  },
  
  agedCreditors: async (asOfDate?: string) => {
    const response = await api.get<ApiResponse<{
      reportDate: string;
      reportTitle: string;
      contacts: {
        contactId: string;
        contactName: string;
        invoices: any[];
        current: string;
        days1to30: string;
        days31to60: string;
        days61to90: string;
        days90plus: string;
        total: string;
      }[];
      totals: {
        current: string;
        days1to30: string;
        days31to60: string;
        days61to90: string;
        days90plus: string;
        total: string;
      };
      summary: {
        totalContacts: number;
        totalInvoices: number;
      };
    }>>('/reports/aged-creditors', { params: asOfDate ? { asOfDate } : {} });
    return response.data;
  },
};

// Organisation API
export interface Organisation {
  id: string;
  name: string;
  slug: string;
  vatNumber: string | null;
  companyNumber: string | null;
  entityType: 'sole_trader' | 'limited_company' | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    country: string;
  };
  settings: {
    currency: string;
    vatRegistered: boolean;
    vatScheme: 'not_registered' | 'standard' | 'flat_rate';
    vatFlatRatePercent: number | null;
    fiscalYearStart: string;
    accountingReferenceDate?: { day: number; month: number } | null;
  };
  invoiceSettings: {
    prefix: string;
    nextNumber: number;
    defaultTerms: string;
    defaultNotes: string;
  };
  quoteSettings: {
    prefix: string;
    nextNumber: number;
    defaultTerms: string;
    defaultNotes: string;
    validityDays: number;
  };
  createdAt: string;
  updatedAt: string;
}

// ----------------------------------------------------------------------------
// Accounting period (Companies House confirm-from-source)
// ----------------------------------------------------------------------------
// The period is confirmed from Companies House, never typed by hand. The preview
// is advisory (it reads back the looked-up company so the user confirms identity
// before anything is written); the set is authoritative (it seals the boundary,
// and refuses once activity has been recorded). settable is the live gate: it is
// only known from the preview, never inferred from on-load state.

export interface RegisteredOffice {
  addressLine1?: string | null;
  addressLine2?: string | null;
  locality?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface AccountingPeriodPreview {
  companyNumber: string;
  companyName: string;
  registeredOffice: RegisteredOffice;
  companyStatus: string;
  type: string;
  entityType: 'limited_company' | null;
  incorporationDate: string;
  accountingReferenceDate: { day: number; month: number };
  periodSource: 'next_accounts' | 'derived';
  period: { yearStart: string; yearEnd: string };
  settable: boolean;
}

export interface AccountingPeriodSetResult {
  companyNumber: string;
  companyName: string;
  entityType: 'limited_company' | null;
  accountingReferenceDate: { day: number; month: number };
  periodSource: 'next_accounts' | 'derived';
  period: { yearStart: string; yearEnd: string };
}

export const organisationApi = {
  get: async () => {
    const response = await api.get<ApiResponse<Organisation>>('/organisation');
    return response.data;
  },

  // Advisory: look up a company and read it back for confirmation. Writes nothing.
  previewAccountingPeriod: async (data: { companyNumber: string }) => {
    const response = await api.post<ApiResponse<AccountingPeriodPreview>>(
      '/organisation/accounting-period/preview',
      data,
    );
    return response.data;
  },

  // Authoritative: confirm and seal the accounting period from Companies House.
  // Refuses with 409 organisation_not_empty once any activity has been recorded.
  setAccountingPeriod: async (data: { companyNumber: string }) => {
    const response = await api.post<ApiResponse<AccountingPeriodSetResult>>(
      '/organisation/accounting-period',
      data,
    );
    return response.data;
  },
  
  update: async (data: {
    name?: string;
    vatNumber?: string | null;
    companyNumber?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    region?: string | null;
    postalCode?: string | null;
    country?: string;
    businessType?: string | null;
  }) => {
    const response = await api.put<ApiResponse<{ id: string; name: string; slug: string; updatedAt: string }>>('/organisation', data);
    return response.data;
  },
  
  updateSettings: async (data: {
    currency?: string;
    vatRegistered?: boolean;
    vatScheme?: 'not_registered' | 'standard' | 'flat_rate';
    vatFlatRatePercent?: number | null;
    fiscalYearStart?: string;
    invoicePrefix?: string;
    invoiceNextNumber?: number;
    invoiceDefaultTerms?: string;
    invoiceDefaultNotes?: string;
    quotePrefix?: string;
    quoteNextNumber?: number;
    quoteDefaultTerms?: string;
    quoteDefaultNotes?: string;
    quoteValidityDays?: number;
  }) => {
    const response = await api.put<ApiResponse<{ message: string }>>('/organisation/settings', data);
    return response.data;
  },
};

// ============================================================================
// Webhooks API (v2.8.0)
// ============================================================================

export interface WebhookEndpoint {
  id: number;
  url: string;
  description: string | null;
  secretHint: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stats24h: { total: number; delivered: number; failed: number };
}

export interface WebhookDelivery {
  id: number;
  eventType: string;
  responseStatus: number | null;
  durationMs: number | null;
  status: 'pending' | 'delivered' | 'failed';
  attempt: number;
  createdAt: string;
  deliveredAt: string | null;
  responseBody: string | null;
}

// ============================================================================
// API Keys (v2.8.0)
// ============================================================================

export const apiKeysApi = {
  list: async () => {
    const response = await api.get<ApiResponse<{ keys: Array<{
      id: number;
      keyPreview: string;
      name: string | null;
      environment: 'live' | 'test';
      createdAt: string;
      lastUsedAt: string | null;
      revokedAt: string | null;
      graceExpiresAt: string | null;
      replacedBy: number | null;
      isActive: boolean;
      isRotating: boolean;
    }> }>>('/api-keys');
    return response.data.data;
  },
  create: async (body: { name?: string; environment: string }) => {
    const response = await api.post<ApiResponse<{
      key: string;
      id: number;
      keyPreview: string;
      name: string | null;
      environment: string;
    }>>('/api-keys', body);
    return response.data.data;
  },
  revoke: async (id: number) => {
    await api.delete(`/api-keys/${id}`);
  },
  rotate: async (id: number) => {
    const response = await api.post<ApiResponse<{
      newKey: {
        id: number;
        key: string;
        keyPreview: string;
        name: string | null;
        environment: string;
        scopes: string[];
        scopesSummary: string;
        createdAt: string;
        warning: string;
      };
      oldKey: {
        id: number;
        keyPreview: string;
        graceExpiresAt: string;
        message: string;
      };
    }>>(`/api-keys/${id}/rotate`);
    return response.data.data;
  },
};

export const webhooksApi = {
  list: async () => {
    const response = await api.get<ApiResponse<{
      endpoints: WebhookEndpoint[];
      availableEvents: string[];
    }>>('/webhooks');
    return response.data;
  },

  create: async (data: { url: string; description?: string; events: string[] }) => {
    const response = await api.post<ApiResponse<WebhookEndpoint>>('/webhooks', data);
    return response.data;
  },

  update: async (id: number, data: { url?: string; description?: string; events?: string[]; isActive?: boolean }) => {
    const response = await api.patch<ApiResponse<WebhookEndpoint>>(`/webhooks/${id}`, data);
    return response.data;
  },

  remove: async (id: number) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/webhooks/${id}`);
    return response.data;
  },

  rotateSecret: async (id: number) => {
    const response = await api.post<ApiResponse<{ id: number; secret: string }>>(`/webhooks/${id}/rotate`);
    return response.data;
  },

  deliveries: async (id: number, page = 1) => {
    const response = await api.get<ApiResponse<{ deliveries: WebhookDelivery[] }>>(`/webhooks/${id}/deliveries?page=${page}`);
    return response.data;
  },
};

// ============================================================================
// API Request Log (v3.1.2)
// ============================================================================

export interface ApiRequestLogEntry {
  id: number;
  apiKeyId: number | null;
  keyPreview: string | null;
  keyName: string | null;
  method: string;
  path: string;
  statusCode: number;
  responseTimeMs: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseBody?: any;
  createdAt: string;
}

export const apiRequestLogApi = {
  list: async (params?: { page?: number; perPage?: number; method?: string; status?: string; keyId?: number }) => {
    const response = await api.get<ApiResponse<{ entries: ApiRequestLogEntry[] }>>('/api-request-log', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ApiResponse<ApiRequestLogEntry>>(`/api-request-log/${id}`);
    return response.data;
  },
};

// ============================================================================
// Billing API
// ============================================================================

/**
 * Step 5 (DESIGN-BILLING-LIFECYCLE section 6): the billing page banner
 * variant, computed by the API. SIBLING DECLARATION of BillingVariant in
 * api/src/routes/billing.ts; drift between the two is an Item 59-class
 * defect. The page renders by exhaustive switch on kind (assertNever),
 * so "the frontend derives nothing" is enforced by the compiler.
 */
export type BillingVariant =
  | { kind: 'grace'; trialEndsAt: string | null; daysLeft: number }
  | { kind: 'grace_expired'; trialEndedAt: string | null }
  | { kind: 'trial'; trialEndsAt: string | null; daysLeft: number | null; firstChargeAmountPence: number | null; firstChargeDate: string | null }
  | { kind: 'trial_ending'; trialEndsAt: string | null; daysLeft: number | null; firstChargeAmountPence: number | null; firstChargeDate: string | null }
  | { kind: 'active'; nextChargeAmountPence: number | null; nextChargeDate: string | null }
  | { kind: 'pending_cancel'; cancelsOn: string | null; paymentFailure: { nextRetryAt: string | null; retryWindowEnds: string | null } | null }
  | { kind: 'past_due'; nextRetryAt: string | null; retryWindowEnds: string | null }
  | { kind: 'suspended'; payUrl: string | null }
  | { kind: 'cancelled'; endedOn: string | null; retentionUntil: string | null };

export interface BillingStatus {
  plan: string;
  /** Section 6 variant, precomputed by the API. The page's single source of banner truth. */
  variant: BillingVariant;
  planName: string;
  hasStripeCustomer: boolean;
  hasSubscription: boolean;
  /** Grace semantics: true while the cardless post-registration window is open. */
  isTrialActive: boolean;
  trialDaysRemaining: number;
  trialEndsAt: string | null;
  /** Stripe's subscription status verbatim; null = never subscribed. */
  subscriptionStatus: string | null;
  /** Founder seat (1..50) once subscribed; null otherwise or after seats fill. */
  seatNumber: number | null;
  subscription: {
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    /** Stripe trial end (ISO) when trialing. */
    trialEnd: string | null;
    /** Recurring amount in pence from the subscription's price. */
    amountPence: number | null;
    card: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    } | null;
    /** Next dunning retry (ISO) when past_due. */
    nextPaymentAttempt: string | null;
  } | null;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  status: 'open' | 'paid' | 'uncollectible';
  created: string;
  totalPence: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export const billingApi = {
  getStatus: async () => {
    const response = await api.get<ApiResponse<BillingStatus>>('/billing/status');
    return response.data;
  },

  getInvoices: async () => {
    const response = await api.get<ApiResponse<{ invoices: BillingInvoice[] }>>('/billing/invoices');
    return response.data;
  },

  getPlans: async () => {
    const response = await api.get<ApiResponse<{ plans: Array<{
      id: string;
      name: string;
      price: number;
      priceFormatted: string;
      features: string[];
    }> }>>('/billing/plans');
    return response.data;
  },

  createCheckout: async (plan: 'sole_trader' | 'limited_company') => {
    const response = await api.post<ApiResponse<{ checkoutUrl: string; sessionId: string }>>('/billing/checkout', { plan });
    return response.data;
  },

  createPortal: async () => {
    const response = await api.post<ApiResponse<{ portalUrl: string }>>('/billing/portal', {});
    return response.data;
  },
};

// ============================================================================
// Categorisation Rules API
// ============================================================================

export interface CategorisationRule {
  id: number;
  name: string;
  description: string | null;
  field: string;
  operator: string;
  value: string;
  targetAccountId: string;
  targetAccountCode: string;
  targetAccountName: string;
  priority: number;
  active: boolean;
  createdAt: string;
  isSeed: boolean;
  updatedAt: string | null;
}

export interface GlobalRule {
  normalisedKey: string;
  name: string;
  field: string;
  operator: string;
  stdCategory: string;
  businessType: string | null;
  confidence: number;
  distinctTenants: number;
  usageCount: number;
  resolvedAccount: {
    code: string;
    name: string;
  };
}

export const categorisationRulesApi = {
  list: async (params?: { active?: boolean }) => {
    const response = await api.get<ApiResponse<{ rules: CategorisationRule[]; total: number }>>('/categorisation-rules', { params });
    return response.data;
  },
  listGlobal: async () => {
    const response = await api.get<ApiResponse<{ rules: GlobalRule[]; total: number }>>('/categorisation-rules/global');
    return response.data;
  },
  create: async (data: {
    name: string;
    field: string;
    operator: string;
    value: string;
    targetAccountId: string;
    description?: string;
    priority?: number;
    active?: boolean;
  }) => {
    const response = await api.post<ApiResponse<CategorisationRule>>('/categorisation-rules', data);
    return response.data;
  },
  update: async (id: number, data: Partial<{
    name: string;
    field: string;
    operator: string;
    value: string;
    targetAccountId: string;
    description: string;
    priority: number;
    active: boolean;
  }>) => {
    const response = await api.patch<ApiResponse<{ id: number; updated: boolean }>>(`/categorisation-rules/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete<ApiResponse<{ id: number; deleted: boolean }>>(`/categorisation-rules/${id}`);
    return response.data;
  },
  test: async (data: {
    description?: string;
    contactName?: string;
    amount?: number;
    reference?: string;
    metadata?: Record<string, string>;
  }) => {
    const response = await api.post<ApiResponse<{
      matched: boolean;
      rule: { id: number; name: string } | null;
      suggestion: { accountId: string; accountCode: string; accountName: string } | null;
      unknownReason?: string | null;
      evidence?: {
        matchedField: string | null;
        operator: string | null;
        matchedValue: string | null;
        normalisedInput: string | null;
        ruleCount: number;
        matchCount: number;
        scoreVector: string | null;
        reason: string;
        provenance: 'LOCAL' | 'VERIFIED' | 'GLOBAL' | 'SYSTEM' | null;
        socialProof: {
          confidence: number;
          distinctTenants: number;
          businessType: string | null;
          displayMessage: string;
        } | null;
        overridden: {
          globalCategory: string;
          localCategory: string;
          distinctTenants: number;
          displayMessage: string;
        } | null;
      };
    }>>('/categorisation-rules/test', data);
    return response.data;
  },
  reorder: async (data: { rules: { id: number; priority: number }[] }) => {
    const response = await api.post<ApiResponse<{ updated: number }>>('/categorisation-rules/reorder', data);
    return response.data;
  },
  categoriseRow: async (data: {
    description: string;
    targetAccountId: string;
    batchDescriptions?: { rowId: number; description: string }[];
  }) => {
    const response = await api.post<ApiResponse<{
      keyword: string;
      normalisedValue: string;
      rule: { id: number; name: string; action: 'created' | 'updated' };
      account: { id: string; code: string; name: string };
      similarRowIds: number[];
    }>>('/categorisation-rules/categorise-row', data);
    return response.data;
  },
};

// ============================================================================
// Bank Imports API (v4.1.0)
// ============================================================================

export interface BankImportRow {
  id: number;
  date: string;
  description: string;
  reference: string | null;
  amount: number;
  balance: number | null;
  status: 'pending' | 'duplicate' | 'error' | 'skipped' | 'created' | 'unmatched' | 'matched';
  matchConfidence: number | null;
  matchMethod: string | null;
  skipReason: string | null;
  transactionId: string | null;
  error: string | null;
  suggestedAccount: {
    id: string;
    code: string;
    name: string;
    ruleName: string;
    provenance?: 'LOCAL' | 'VERIFIED' | 'GLOBAL' | null;
    socialProof?: {
      confidence: number;
      distinctTenants: number;
      businessType: string | null;
      displayMessage: string;
    } | null;
  } | null;
}

export interface BankImportUploadResponse {
  importId: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
  ignoredRows?: number;
  duplicates: number;
  dateRange: { from: string; to: string } | null;
  preview: BankImportRow[];
  // TMADD 3.0 — UIE schema metadata
  schema?: {
    fingerprint: string;
    provenance: 'LOCAL' | 'VERIFIED' | 'GLOBAL' | 'USER_CORRECTED';
    anchorRowIndex: number;
    columnMapping: Record<string, string>;
    dateFormat: string;
    amountModel: string;
    hasBalanceColumn: boolean;
    detectedPreset: string | null;
    engineVersion: string;
  };
  balanceProof?: {
    status: 'VERIFIED' | 'FAILED' | 'NOT_APPLICABLE';
    openingBalance: number;
    closingBalance: number;
    delta: number;
    stepwisePass: boolean | null;
  } | null;
}

export interface BankImportDetail {
  import: {
    id: string;
    accountId: string;
    filename: string;
    importDate: string;
    statementFrom: string;
    statementTo: string;
    totalRows: number;
    matchedRows: number;
    createdRows: number;
    skippedRows: number;
    status: string;
    errorMessage: string | null;
  };
  rows: BankImportRow[];
}

export interface BankImportConfirmResponse {
  importId: string;
  created: number;
  skipped: number;
  total: number;
}

export const bankImportsApi = {
  getBankAccounts: async () => {
    const response = await api.get<ApiResponse<{ bankAccounts: any[] }>>('/bank-accounts');
    return response.data;
  },

upload: async (file: File, bankAccountId: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bankAccountId', bankAccountId.toString());
    const response = await api.post<ApiResponse<BankImportUploadResponse>>('/bank-imports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getImport: async (importId: string, page = 1, perPage = 50) => {
    const response = await api.get<ApiResponse<BankImportDetail>>(`/bank-imports/${importId}`, {
      params: { page, perPage },
    });
    return response.data;
  },

  confirm: async (importId: string, rowIds: number[], defaultAccountId?: string, rowAccountMap?: Record<string, string>) => {
    const response = await api.post<ApiResponse<BankImportConfirmResponse>>(`/bank-imports/${importId}/confirm`, {
      rows: rowIds,
      ...(defaultAccountId && { defaultAccountId }),
      ...(rowAccountMap && Object.keys(rowAccountMap).length > 0 && { rowAccountMap }),
    });
    return response.data;
  },

  list: async (page = 1, perPage = 20) => {
    const response = await api.get<ApiResponse<{ imports: any[] }>>('/bank-imports', {
      params: { page, perPage },
    });
    return response.data;
  },

  delete: async (importId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/bank-imports/${importId}`);
    return response.data;
  },

  remap: async (importId: string, columnMapping: Record<string, string>,
                dateFormat?: string, amountModel?: string) => {
    const response = await api.post<ApiResponse<BankImportUploadResponse>>(
      `/bank-imports/${importId}/remap`,
      { columnMapping, ...(dateFormat && { dateFormat }), ...(amountModel && { amountModel }) },
    );
    return response.data;
  },
};

// ============================================================================
// Bank Accounts API
// Separate export so create can be imported without bankImportsApi.
// ============================================================================

export const bankAccountsApi = {
  createBankAccount: async (data: {
    bankName: string;
    accountName?: string;
    sortCode?: string;
    accountNumber?: string;
  }) => {
    const response = await api.post<ApiResponse<{ bankAccount: {
      id: number;
      accountId: number;
      bankName: string;
      accountName: string | null;
      sortCode: string | null;
      accountNumber: string | null;
      isPrimary: boolean;
    } }>>('/bank-accounts', data);
    return response.data;
  },
};

// ============================================================================
// Directors API (v4.7.0 — Ltd company only)
// ============================================================================

export interface Director {
  id: string;
  fullName: string;
  isShareholder: boolean;
  sharesHeld: number | null;
  shareClass: string;
  appointedDate: string | null;
  resignedDate: string | null;
  active: boolean;
  connectedToDirectorId: string | null;
  connectedToDirectorName: string | null;
  relationship: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DirectorDetail extends Director {
  userId: number | null;
  loanBalance?: {
    balancePence: number;
    transactionCount: number;
    isOverdrawn: boolean;
  };
}

export const directorsApi = {
  list: async () => {
    const response = await api.get<ApiResponse<{ directors: Director[] }>>('/directors');
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<DirectorDetail>>(`/directors/${id}`);
    return response.data;
  },

  create: async (data: {
    fullName: string;
    isShareholder?: boolean;
    sharesHeld?: number | null;
    shareClass?: string;
    appointedDate?: string | null;
    connectedToDirectorId?: number | null;
    relationship?: string | null;
  }) => {
    const response = await api.post<ApiResponse<Director>>('/directors', data);
    return response.data;
  },

  update: async (id: string, data: {
    fullName?: string;
    isShareholder?: boolean;
    sharesHeld?: number | null;
    shareClass?: string;
    appointedDate?: string | null;
    resignedDate?: string | null;
    active?: boolean;
    connectedToDirectorId?: number | null;
    relationship?: string | null;
  }) => {
    const response = await api.patch<ApiResponse<Director>>(`/directors/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/directors/${id}`);
    return response.data;
  },

  // DLA
  getLoanBalance: async (id: string) => {
    const response = await api.get<ApiResponse<{
      directorId: string;
      directorName: string;
      balancePence: number;
      transactionCount: number;
      isOverdrawn: boolean;
      warnings: Array<{ type: string; severity: string; message: string; details?: any }>;
    }>>(`/director-loans/balance/${id}`);
    return response.data;
  },

  listLoans: async (id: string) => {
    const response = await api.get<ApiResponse<{
      transactions: Array<{
        id: string;
        date: string;
        description: string;
        amountPence: number;
        loanType: string;
        notes: string | null;
        createdAt: string;
      }>;
      balance: number;
    }>>(`/director-loans?directorId=${id}`);
    return response.data;
  },

  listDividends: async (id: string) => {
    const response = await api.get<ApiResponse<{
      dividends: Array<{
        id: string;
        boardMinutesId: string;
        date: string;
        amountPence: number;
        voucherNumber: string;
        status: string;
        paymentMethod: string;
        shareClass: string;
        paidDate: string | null;
      }>;
    }>>(`/dividends?directorId=${id}`);
    return response.data;
  },

  // DLA Snapshots
  createSnapshot: async (directorId: string, data: { periodEndDate: string }) => {
    const response = await api.post<ApiResponse<{
      id: string;
      directorId: string;
      directorName: string | null;
      periodEndDate: string;
      balancePence: number;
      s455Deadline: string;
      s455Rate: number;
      s455ExposurePence: number;
      s455Status: string;
      bikApplicable: boolean;
      bikAmountPence: number;
      bikOfficialRate: number | null;
      bikAverageBalancePence: number;
      bikOverdrawnDays: number;
      p11dDeadline: string | null;
      createdAt: string;
      updatedAt: string;
    }>>('/director-loans/snapshot', {
      directorId,
      periodEndDate: data.periodEndDate,
    });
    return response.data;
  },

  listSnapshots: async (directorId: string) => {
    const response = await api.get<ApiResponse<{
      snapshots: Array<{
        id: string;
        directorId: string;
        directorName: string | null;
        periodEndDate: string;
        balancePence: number;
        s455Deadline: string;
        s455Rate: number;
        s455ExposurePence: number;
        s455Status: string;
        bikApplicable: boolean;
        bikAmountPence: number;
        bikOfficialRate: number | null;
        bikAverageBalancePence: number;
        bikOverdrawnDays: number;
        p11dDeadline: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
    }>>('/director-loans/snapshots', { params: { directorId } });
    return response.data;
  },
};

// Dividends API
export const dividendsApi = {
  profitCheck: async (financialYearEnd: string) => {
    const response = await api.get<ApiResponse<{
      financialYearEnd: string;
      retainedEarningsPence: number;
      previouslyDeclaredPence: number;
      availableProfitPence: number;
      canDeclareDividend: boolean;
    }>>(`/dividends/profit-check?financialYearEnd=${financialYearEnd}`);
    return response.data;
  },

  createBoardMinutes: async (data: {
    date: string;
    financialYearEnd: string;
    attendees: string[];
    retainedProfitPence: number;
    totalDividendPence: number;
    notes?: string;
  }) => {
    const response = await api.post<ApiResponse<{
      id: string;
      date: string;
      financialYearEnd: string;
      totalDividendPence: number;
      totalDeclaredPence: number;
      remainingPence: number;
      status: string;
    }>>('/dividends/board-minutes', data);
    return response.data;
  },

  listBoardMinutes: async (params?: { financialYear?: string; status?: string }) => {
    const response = await api.get<ApiResponse<{
      boardMinutes: Array<{
        id: string;
        date: string;
        financialYearEnd: string;
        totalDividendPence: number;
        totalDeclaredPence: number;
        remainingPence: number;
        status: string;
        notes: string | null;
      }>;
    }>>('/dividends/board-minutes', { params });
    return response.data;
  },

  getBoardMinutes: async (id: string) => {
    const response = await api.get<ApiResponse<{
      id: string;
      date: string;
      financialYearEnd: string;
      totalDividendPence: number;
      totalDeclaredPence: number;
      remainingPence: number;
      status: string;
      dividends: Array<{
        id: string;
        directorName: string;
        amountPence: number;
        voucherNumber: string;
        status: string;
      }>;
    }>>(`/dividends/board-minutes/${id}`);
    return response.data;
  },

  declare: async (data: {
    boardMinutesId: string;
    directorId: string;
    date: string;
    amountPence: number;
    shareClass: string;
    paymentMethod: string;
    createJournal: boolean;
    notes?: string;
  }) => {
    const response = await api.post<ApiResponse<{
      id: string;
      directorId: string;
      directorName: string;
      amountPence: number;
      voucherNumber: string;
      status: string;
      paymentMethod: string;
    }>>('/dividends', data);
    return response.data;
  },

  list: async (params?: {
    directorId?: string;
    financialYear?: string;
    page?: number;
    perPage?: number;
  }) => {
    const response = await api.get<ApiResponse<{
      dividends: Array<{
        id: string;
        boardMinutesId: string;
        directorId: string;
        directorName: string | null;
        date: string;
        amountPence: number;
        shareClass: string;
        paymentMethod: string;
        status: string;
        paidDate: string | null;
        voucherNumber: string;
        notes: string | null;
      }>;
    }>>('/dividends', { params });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<{
      id: string;
      directorId: string;
      directorName: string | null;
      date: string;
      amountPence: number;
      paymentMethod: string;
      status: string;
      paidDate: string | null;
      voucherNumber: string;
    }>>(`/dividends/${id}`);
    return response.data;
  },

  markPaid: async (id: string, paidDate?: string) => {
    const response = await api.patch<ApiResponse<{
      id: string;
      status: string;
      paidDate: string;
      amountPence: number;
      voucherNumber: string;
    }>>(`/dividends/${id}/pay`, {
      paidDate: paidDate || new Date().toISOString().split('T')[0],
    });
    return response.data;
  },

  downloadVoucher: async (id: string): Promise<Blob> => {
    const response = await api.get(`/dividends/${id}/voucher`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  calculateTax: async (data: {
    grossDividendPence: number;
    otherIncomePence?: number;
    personalAllowanceUsedPence?: number;
  }) => {
    const response = await api.post<ApiResponse<{
      input: { grossDividendPence: number; otherIncomePence: number; personalAllowancePence: number };
      calculation: {
        dividendAllowancePence: number;
        taxableAfterAllowancePence: number;
        basicRate: { rate: number; taxPence: number };
        higherRate: { rate: number; taxPence: number };
        additionalRate: { rate: number; taxPence: number };
        totalTaxPence: number;
        effectiveRate: number;
        netDividendPence: number;
      };
      taxYear: string;
    }>>('/dividends/tax-calculator', data);
    return response.data;
  },

  summary: async (financialYearEnd?: string) => {
    const params = financialYearEnd ? { financialYearEnd } : undefined;
    const response = await api.get<ApiResponse<{
      financialYearEnd: string | null;
      directors: Array<{
        directorId: string;
        fullName: string;
        sharesHeld: number | null;
        shareClass: string;
        totalDeclaredPence: number;
        totalPaidPence: number;
        totalUnpaidPence: number;
        dividendCount: number;
      }>;
      totalDeclaredPence: number;
      totalPaidPence: number;
      totalUnpaidPence: number;
    }>>('/dividends/summary', { params });
    return response.data;
  },
};

export const contactImportsApi = {
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ApiResponse<any>>('/contact-imports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getImport: async (importId: string) => {
    const response = await api.get<ApiResponse<any>>(`/contact-imports/${importId}`);
    return response.data;
  },

  confirm: async (importId: string) => {
    const response = await api.post<ApiResponse<any>>(`/contact-imports/${importId}/confirm`, {});
    return response.data;
  },

  resolve: async (importId: string, resolutions: Array<{ link?: [string, string]; split?: [string, string]; skip?: string }>) => {
    const response = await api.post<ApiResponse<any>>(`/contact-imports/${importId}/resolve`, { resolutions });
    return response.data;
  },

  list: async (page = 1, perPage = 20) => {
    const response = await api.get<ApiResponse<any>>('/contact-imports', {
      params: { page, perPage },
    });
    return response.data;
  },

  delete: async (importId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/contact-imports/${importId}`);
    return response.data;
  },
};

// ============================================================================
// Opening Balances API (v5.5.0 — TMADD 5.0 OBCE)
// ============================================================================

export const openingBalancesApi = {
  status: async () => {
    const response = await api.get<ApiResponse<{
      hasOpeningBalance: boolean;
      transactionId: string | null;
    }>>('/opening-balances/status');
    return response.data;
  },

  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ApiResponse<{
      importId: string;
      filename: string;
      cutoverDate: string;
      canConfirm: boolean;
      stats: {
        totalRows: number;
        validRows: number;
        ignoredRows: number;
        errorRows: number;
        journalLines: number;
        totalDebit: number;
        totalCredit: number;
      };
      mappings: Array<{
        sourceLabel: string;
        targetAccountId: string;
        targetCode: string;
        targetName: string;
        targetType: string;
        method: string;
        confidence: number;
      }>;
      unmapped: Array<{ sourceLabel: string; rowIndices: number[] }>;
      lines: Array<{
        accountId: string;
        accountCode: string;
        accountName: string;
        accountType: string;
        debit: string;
        credit: string;
        isRounding: boolean;
        sourceLabel: string;
      }>;
      balanceProof: {
        totalDebit: string;
        totalCredit: string;
        delta: string;
        balanced: boolean;
        roundingInjected: boolean;
        roundingAmount: string;
      };
      errors: Array<{
        code: string;
        message: string;
        rowIndex?: number;
        detail?: string;
      }>;
    }>>('/opening-balances/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  get: async (importId: string) => {
    const response = await api.get<ApiResponse<any>>(`/opening-balances/${importId}`);
    return response.data;
  },

  map: async (importId: string, data: {
    mappings?: Array<{ sourceLabel: string; targetAccountId: string }>;
    cutoverDate?: string;
  }) => {
    const response = await api.patch<ApiResponse<any>>(`/opening-balances/${importId}/map`, data);
    return response.data;
  },

  confirm: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      transactionId: string;
      journalLines: number;
      totalDebit: string;
      totalCredit: string;
      cutoverDate: string;
      reference: string;
    }>>(`/opening-balances/${importId}/confirm`, {});
    return response.data;
  },

  void: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      voided: boolean;
      transactionId: string;
      message: string;
    }>>(`/opening-balances/${importId}/void`, {});
    return response.data;
  },

  delete: async (importId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/opening-balances/${importId}`);
    return response.data;
  },
};

// ============================================================================
// Invoice Imports API (v5.6.0 — TMADD 6.0 ODCE: Receivables)
// ============================================================================

export const invoiceImportsApi = {
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ApiResponse<any>>('/invoice-imports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  get: async (importId: string) => {
    const response = await api.get<ApiResponse<any>>(`/invoice-imports/${importId}`);
    return response.data;
  },

  confirm: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      documentsCreated: number;
      syntheticPaymentsCreated: number;
      clearingBalance: number;
      direction: string;
      engineVersion: string;
    }>>(`/invoice-imports/${importId}/confirm`, {});
    return response.data;
  },

  void: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      voided: boolean;
      importId: string;
      message: string;
    }>>(`/invoice-imports/${importId}/void`, {});
    return response.data;
  },

  delete: async (importId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/invoice-imports/${importId}`);
    return response.data;
  },
};

// ============================================================================
// Bill Imports API (v5.6.0 — TMADD 6.0 ODCE: Payables)
// ============================================================================

export const billImportsApi = {
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ApiResponse<any>>('/bill-imports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  get: async (importId: string) => {
    const response = await api.get<ApiResponse<any>>(`/bill-imports/${importId}`);
    return response.data;
  },

  confirm: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      documentsCreated: number;
      syntheticPaymentsCreated: number;
      clearingBalance: number;
      direction: string;
      engineVersion: string;
    }>>(`/bill-imports/${importId}/confirm`, {});
    return response.data;
  },

  void: async (importId: string) => {
    const response = await api.post<ApiResponse<{
      voided: boolean;
      importId: string;
      message: string;
    }>>(`/bill-imports/${importId}/void`, {});
    return response.data;
  },

  delete: async (importId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/bill-imports/${importId}`);
    return response.data;
  },
};

export interface MigrationSummary {
  id: string;
  provider: string;
  providerLabel: string;
  cutoverDate: string;
  status: string;
  contactsImportId: string | null;
  balancesImportId: string | null;
  invoicesImportId: string | null;
  billsImportId: string | null;
  reconciliation: Record<string, unknown> | null;
  warnings: unknown[];
  createdAt: string;
  completedAt: string | null;
}

export interface MigrationDetail extends MigrationSummary {
  imports: Record<string, {
    importId: string;
    importType: string;
    status: string;
    totalRows: number;
    createdCount: number;
    skippedCount: number;
    errorCount: number;
    filename: string;
  }>;
  providerConfig: {
    provider: string;
    label: string;
    fileHints: string[];
    expectedArtefacts: {
      contacts: boolean;
      openingBalances: boolean;
      invoices: boolean;
      bills: boolean;
    };
  } | null;
}

export interface MigrationValidation {
  ready: boolean;
  gates: Array<{
    code: string;
    passed: boolean;
    message: string;
  }>;
}

export const migrationApi = {
  start: async (provider: string, cutoverDate: string) => {
    const response = await api.post<ApiResponse<MigrationSummary>>('/migration', {
      provider,
      cutoverDate,
    });
    return response.data;
  },

  get: async (migrationId: string) => {
    const response = await api.get<ApiResponse<MigrationDetail>>(`/migration/${migrationId}`);
    return response.data;
  },

  upload: async (migrationId: string, importIds: {
    contactsImportId?: string | null;
    balancesImportId?: string | null;
    invoicesImportId?: string | null;
    billsImportId?: string | null;
  }) => {
    const response = await api.post<ApiResponse<MigrationSummary>>(
      `/migration/${migrationId}/upload`,
      importIds,
    );
    return response.data;
  },

  validate: async (migrationId: string) => {
    const response = await api.post<ApiResponse<MigrationValidation>>(
      `/migration/${migrationId}/validate`,
      {},
    );
    return response.data;
  },

  execute: async (migrationId: string) => {
    const response = await api.post<ApiResponse<{
      migrationId: string;
      status: string;
      reconciliation: Record<string, unknown>;
    }>>(`/migration/${migrationId}/execute`, {});
    return response.data;
  },

  history: async () => {
    const response = await api.get<ApiResponse<{
      migrations: MigrationSummary[];
    }>>('/migration/history');
    return response.data;
  },

  abandon: async (migrationId: string) => {
    const response = await api.delete<ApiResponse<{
      abandoned: boolean;
      migrationId: string;
      message: string;
    }>>(`/migration/${migrationId}`);
    return response.data;
  },
};

export default api;
